import type { operationDefs } from "@akashnetwork/console-api-types";

export type ProjectOutput = operationDefs["listProjects"]["responses"][200]["content"]["application/json"]["data"][number];

/** A project of the active organization as the list returns it, with its deployments, last activity and what the caller may do in it. */
export type Project = ProjectOutput;

/** A project as creating or reading one returns it, without what only the list carries. */
export type ProjectRecord = operationDefs["getProject"]["responses"][200]["content"]["application/json"]["data"];

/** What a project folder shows of its open deployments: how many there are and the newest few. */
export type ProjectDeploymentSummary = NonNullable<Project["deployments"]>;

export type ProjectDeploymentPreview = ProjectDeploymentSummary["preview"][number];

export type ProjectDeploymentStatus = ProjectDeploymentPreview["status"];

export type CreateProjectInput = operationDefs["createProject"]["requestBody"]["content"]["application/json"];

/** The `code` of the 409 `createProject` returns when the active organization already has a project by that name. */
export const PROJECT_NAME_TAKEN_ERROR_CODE = "project_name_taken";

/** The `code` of the 403 `createProject` returns when the active organization already holds as many live projects as it may. */
export const PROJECT_LIMIT_REACHED_ERROR_CODE = "project_limit_reached";

/** The `code` of the 409 `deleteProject` answers for the organization's default project. */
export const PROJECT_IS_DEFAULT_ERROR_CODE = "project_is_default";

/** The `code` of the 409 `deleteProject` answers while the project still holds an open deployment. */
export const PROJECT_NOT_EMPTY_ERROR_CODE = "project_not_empty";

export const MAX_PROJECT_SLUG_LENGTH = 40;

export const MAX_PROJECT_DESCRIPTION_LENGTH = 140;

export type ProjectMember = operationDefs["listProjectMembers"]["responses"][200]["content"]["application/json"]["data"][number];

export type ProjectRole = ProjectMember["role"];

/** Where a deployment is filed; `projectId` is null for one the console has not filed into a project yet. */
export type DeploymentLocation = operationDefs["getDeploymentLocation"]["responses"][200]["content"]["application/json"]["data"];

/** The `code` of the 400 `createDeployment` answers when the caller may write to no default project and named none. */
export const PROJECT_REQUIRED_ERROR_CODE = "project_required";

/** The message of the 404 the api answers for a project that was deleted or that the caller can no longer reach. */
export const PROJECT_NOT_FOUND_MESSAGE = "Project not found";

export const PROJECT_ACCESS_ERROR_CODES = {
  forbidden: "forbidden",
  implicitProjectAccess: "implicit_project_access",
  billingRoleNotGrantable: "billing_role_not_grantable",
  alreadyGranted: "already_granted"
} as const;
